import { useState } from "react";
import { AlertTriangle, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { createComplaint } from "@/lib/api";

const CATEGORIES = ["Driver conduct", "Vehicle condition", "Timekeeping", "Booking / dispatch", "Pricing", "Accessibility", "Data / privacy", "Other"];

const inputCls = "bg-surface border-transparent rounded-xl h-12 px-4 text-ink placeholder:text-ink/35 focus:bg-white focus:border-brand focus-visible:ring-2 focus-visible:ring-brand/20 transition-colors";
const labelCls = "text-[11px] font-bold text-ink/50 uppercase tracking-[0.12em] mb-2 block";

const empty = { name: "", email: "", phone: "", booking_ref: "", incident_date: "", category: "Driver conduct", message: "" };

export default function Complaints() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and a description.");
      return;
    }
    setLoading(true);
    try {
      await createComplaint({ ...form, incident_date: form.incident_date || null });
      setDone(true);
      toast.success("Complaint submitted. We'll acknowledge within 2 working days.");
    } catch (err) {
      toast.error("Something went wrong. Please email bookings@clocktowercarsuk.com.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <Seo title="Submit a Complaint" description="Raise a complaint with Clocktower Cars Gatwick. Acknowledged within 2 working days, resolved within 10 working days." path="/complaints" />

      <section className="bg-ink py-20 md:py-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.25em] mb-4 text-brand-light">Complaints</p>
          <h1 className="font-heading text-4xl md:text-5xl font-black text-white tracking-tight leading-[1.05]">
            Tell us what went wrong
          </h1>
          <p className="mt-5 text-white/60 max-w-xl leading-relaxed">
            We take every complaint seriously. Fill in the form below and we will acknowledge within 2 working days and aim to respond fully within 10.
          </p>
        </div>
      </section>

      <section className="bg-white py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          {done ? (
            <div data-testid="complaint-success" className="bg-surface border border-slate-200 rounded-2xl p-10 text-center">
              <div className="grid place-items-center h-14 w-14 rounded-full bg-brand/10 text-brand mx-auto mb-5">
                <CheckCircle2 className="h-7 w-7" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-ink mb-2">Thank you</h2>
              <p className="text-ink/60 max-w-md mx-auto">Your complaint has been logged. A member of our team will acknowledge it within 2 working days at {form.email}.</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="complaint-form" className="grid gap-6">
              <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-xl p-4 flex gap-3 items-start">
                <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0" />
                <p className="text-sm leading-relaxed">
                  If you are in immediate danger please call 999. For urgent, in-journey issues call our 24/7 line on <a href="tel:01293515151" className="font-semibold underline">01293 515151</a>.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className={labelCls}>Your name *</Label>
                  <Input data-testid="complaint-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Full name" required />
                </div>
                <div>
                  <Label className={labelCls}>Email *</Label>
                  <Input data-testid="complaint-email" type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" required />
                </div>
                <div>
                  <Label className={labelCls}>Phone</Label>
                  <Input data-testid="complaint-phone" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Optional" />
                </div>
                <div>
                  <Label className={labelCls}>Booking reference</Label>
                  <Input data-testid="complaint-ref" className={inputCls} value={form.booking_ref} onChange={(e) => set("booking_ref", e.target.value)} placeholder="If known" />
                </div>
                <div>
                  <Label className={labelCls}>Incident date</Label>
                  <Input data-testid="complaint-date" type="date" className={inputCls} value={form.incident_date} onChange={(e) => set("incident_date", e.target.value)} />
                </div>
                <div>
                  <Label className={labelCls}>Category *</Label>
                  <Select value={form.category} onValueChange={(v) => set("category", v)}>
                    <SelectTrigger data-testid="complaint-category" className={inputCls}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className={labelCls}>What happened? *</Label>
                <Textarea
                  data-testid="complaint-message"
                  className={`${inputCls} h-40 py-3`}
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  placeholder="Please include what happened, where, and any driver or vehicle details you remember."
                  required
                />
              </div>

              <Button data-testid="complaint-submit" type="submit" disabled={loading} className="bg-brand hover:bg-brand-light text-white h-14 rounded-full font-bold text-base">
                {loading ? <><Loader2 className="h-5 w-5 animate-spin mr-2" /> Submitting…</> : "Submit complaint"}
              </Button>
              <p className="text-xs text-ink/40 text-center leading-relaxed">
                By submitting, you agree we may use these details to investigate and respond to your complaint. See our Privacy Policy for how your data is handled.
              </p>
            </form>
          )}
        </div>
      </section>
    </Layout>
  );
}
